import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

// Show sooner, so it stays visible while the user scrolls back up.
const SCROLL_SHOW_Y = 120;

const ScrollToTopButton = () => {
  const [visible, setVisible] = useState(false);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  }, []);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY || document.documentElement.scrollTop || 0;
        setVisible(y > SCROLL_SHOW_Y);
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  if (!visible) return null;

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  };

  const button = (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Scroll to top"
      title="Back to top"
      className="fixed bottom-6 right-6 h-12 w-12 rounded-md bg-black/90 hover:bg-black text-yellow-400 shadow-lg ring-1 ring-white/10 flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-400/70"
      style={{ zIndex: 9999 }}
    >
      <svg
        className="h-6 w-6"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 14l6-6 6 6" />
      </svg>
    </button>
  );

  // Render into document.body to avoid any parent CSS (transform/filter) breaking `position: fixed`.
  if (typeof document === 'undefined') return null;
  return createPortal(button, document.body);
};

export default ScrollToTopButton;

